import React, { createContext, useContext, useCallback, useState, useEffect, useRef, ReactNode } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import { jeopardyGameState as jeopardyGameStateSubscription } from 'graphql/subscriptions';
import { upsertJeopardyGameState } from 'graphql/mutations';
import { JeopardyGameState, DEFAULT_JEOPARDY_GAME_STATE } from './jeopardy-types';

interface JeopardyContextType {
    gameState: JeopardyGameState;
    sessionProgressId: number | null;
    loading: boolean;
    error: any;
    updateGameState: (patch: Partial<JeopardyGameState>) => Promise<void>;
}

const JeopardyContext = createContext<JeopardyContextType | null>(null);

export function useJeopardyContext() {
    const ctx = useContext(JeopardyContext);
    if (!ctx) throw new Error('useJeopardyContext must be used within JeopardyProvider');
    return ctx;
}

interface JeopardyProviderProps {
    sessionId: number;
    children: ReactNode;
}

export function JeopardyProvider({ sessionId, children }: JeopardyProviderProps) {
    const { data, loading, error } = useSubscription(jeopardyGameStateSubscription, {
        variables: { sessionId },
    });

    const [upsertGameStateMutation] = useMutation(upsertJeopardyGameState);

    const progressRows = data?.quiz_master_session_progress ?? [];
    const progressRow = progressRows[0] ?? null;
    const sessionProgressId: number | null = progressRow?.id ?? null;

    const serverGameState: JeopardyGameState =
        progressRow?.additional_info ?? DEFAULT_JEOPARDY_GAME_STATE;

    // Local state mirrors the server state. A ref is kept in sync so that
    // updateGameState can always read the latest snapshot without stale closures.
    const [localGameState, setLocalGameState] = useState<JeopardyGameState>(serverGameState);
    const localGameStateRef = useRef(localGameState);

    // Sync from subscription: whenever the server sends new data, update local state.
    // This runs after each subscription update, safely replacing any optimistic state
    // with the confirmed server value.
    useEffect(() => {
        setLocalGameState(serverGameState);
        localGameStateRef.current = serverGameState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const updateGameState = useCallback(
        async (patch: Partial<JeopardyGameState>) => {
            if (!sessionProgressId) return;
            // Read the latest state from the ref to avoid stale closures and to
            // guarantee `next` is defined before calling the mutation.
            const next: JeopardyGameState = { ...localGameStateRef.current, ...patch };
            localGameStateRef.current = next;
            setLocalGameState(next);
            await upsertGameStateMutation({
                variables: {
                    sessionProgressId,
                    additionalInfo: next,
                },
            });
        },
        [sessionProgressId, upsertGameStateMutation]
    );

    return (
        <JeopardyContext.Provider
            value={{
                gameState: localGameState,
                sessionProgressId,
                loading,
                error,
                updateGameState,
            }}
        >
            {children}
        </JeopardyContext.Provider>
    );
}
